import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '@/stores/auth';
import { paymentsApi, coursesApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Loader2, Shield, ArrowLeft, Tag, Check, X } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { formatPrice } from '@/lib/utils';

export default function CheckoutPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { user, isAuthenticated } = useAuth();
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
  const [paymentError, setPaymentError] = useState("");
  const [couponError, setCouponError] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/checkout/${courseId}` } });
    }
  }, [isAuthenticated, navigate, courseId]);

  // Fetch course details
  const { data: courses } = useQuery({
    queryKey: ['courses'],
    queryFn: () => coursesApi.list(),
  });

  const course = courses?.find((c: any) => c.id === courseId);

  // Validate coupon
  const validateCoupon = useMutation({
    mutationFn: () => paymentsApi.validateCoupon(couponCode, courseId!),
    onSuccess: (data) => {
      setAppliedCoupon(data);
      setCouponError('');
      toast({ title: 'Coupon applied!', description: `You saved ${formatPrice(data.discount)}` });
    },
    onError: (error: any) => {
      setPaymentError(error.message || 'Failed to initiate payment.');
      setAppliedCoupon(null);
      setCouponError(error.message || 'Invalid coupon code');
    },
  });

  // Initiate payment
  const initiatePayment = useMutation({
    mutationFn: () => paymentsApi.initiate(courseId!, 'en', appliedCoupon?.code),
    onSuccess: (data) => {
      if (data.redirectUrl) {
        window.location.href = data.redirectUrl;
      }
    },
    onError: (error: any) => {
      setPaymentError(error.message || 'Failed to initiate payment.');
      toast({ title: 'Payment Error', description: error.message || 'Failed to initiate payment.', variant: 'destructive' });
    },
  });

  const handleApplyCoupon = () => {
    if (!couponCode.trim()) return;
    setCouponError('');
    validateCoupon.mutate();
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  const handlePayment = () => {
    setPaymentError("");
    initiatePayment.mutate();
  };

  if (!isAuthenticated) return null;

  const originalPrice = course?.price || 0;
  const finalPrice = appliedCoupon ? appliedCoupon.finalPrice : originalPrice;
  const discount = appliedCoupon ? appliedCoupon.discount : 0;

  return (
    <div className="soleil min-h-screen flex items-center justify-center py-12 px-4" style={{ background: "var(--soleil-cream)" }}>
      <Card className="w-full max-w-lg">
        <CardContent className="p-8">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
            <ArrowLeft className="h-4 w-4" /> {isRTL ? 'رجوع' : 'Back'}
          </button>

          <h2 className="text-2xl font-bold mb-1">{isRTL ? 'الدفع' : 'Checkout'}</h2>
          <p className="text-muted-foreground mb-6">{isRTL ? 'أكمل عملية التسجيل' : 'Complete your enrollment'}</p>

          {/* Course Summary */}
          {course && (
            <div className="bg-muted/50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold">{course.titleEn}</h3>
              <p className="text-sm text-muted-foreground mt-1">{course.shortDescriptionEn}</p>
            </div>
          )}

          {/* Coupon Input */}
          <div className="mb-6">
            <label className="text-sm font-medium mb-2 block">{isRTL ? 'لديك كود خصم؟' : 'Have a coupon code?'}</label>
            {appliedCoupon ? (
              <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-md px-4 py-3">
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-600" />
                  <span className="font-medium text-green-700">{appliedCoupon.code}</span>
                  <span className="text-sm text-green-600">
                    ({appliedCoupon.discountType === 'percentage' ? `${appliedCoupon.discountValue}% off` : `${formatPrice(appliedCoupon.discountValue)} off`})
                  </span>
                </div>
                <button onClick={handleRemoveCoupon} className="text-green-600 hover:text-green-800">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder={isRTL ? "أدخل كود الخصم" : "Enter coupon code"}
                    className="pl-9"
                    onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={handleApplyCoupon}
                  disabled={validateCoupon.isPending || !couponCode.trim()}
                >
                  {validateCoupon.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : (isRTL ? 'تطبيق' : 'Apply')}
                </Button>
              </div>
            )}
            {couponError && (
              <p className="text-sm text-red-600 mt-2">{couponError}</p>
            )}
          </div>

          {/* Price Summary */}
          <div className="border-t pt-4 mb-6 space-y-2">
            <div className="flex justify-between text-sm">
              <span>{isRTL ? 'سعر الدورة' : 'Course Price'}</span>
              <span>{formatPrice(originalPrice)}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Discount</span>
                <span>-{formatPrice(discount)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-lg border-t pt-2">
              <span>{isRTL ? 'الإجمالي' : 'Total'}</span>
              <span className="text-primary">{formatPrice(finalPrice)}</span>
            </div>
          </div>

          {paymentError && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-4 py-3 text-sm mb-4">{paymentError}</div>
          )}
          {/* Pay Button */}
          <Button
            className="w-full"
            size="lg"
            onClick={handlePayment}
            disabled={initiatePayment.isPending}
          >
            {initiatePayment.isPending ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing...</>
            ) : (
              isRTL ? `ادفع ${formatPrice(finalPrice)}` : `Pay ${formatPrice(finalPrice)}`
            )}
          </Button>

          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <Shield className="h-4 w-4" />
            <span>{isRTL ? 'دفع آمن عبر Qi Card' : 'Secure payment powered by Qi Card'}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
